import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useUserContext } from 'contexts/user';
import { useThemeContext } from 'contexts';
import TextField from 'components/control/text-field';
import TextArea from 'components/control/text-area';
import Button from 'components/control/button';
import { trackEvent } from 'utils/google-tag-manager';
import { widgetEvents } from 'utils/widget/analytics';
import { useRouter } from 'next/router';
import SuccessMessage from 'components/connection-request/success-message';
import Snackbar from 'components/snackbar';
import { createConnectionRequest } from 'data/connection-request';
import { buildClassName } from 'utils/build-class-name';
import { GTM_CLICK_APPRAISAL_DETAIL_ESTIMATE_FOR_FREE_POPUP, GTM_CLICK_APPRAISAL_ENTER_EMAIL, GTM_CLICK_APPRAISAL_NO_ESTIMATE_DETAIL_ESTIMATE_FOR_FREE_POPUP } from 'constants/events';
import styles from './style.module.scss';
import { useTranslation } from 'next-i18next';
import { LOCALE_HOME_APPRAISAL_PATHS, Namespaces } from 'types/locales';

import type Listing from 'data/listing';

export type ListingInfoType = Pick<Listing, 'isAvailable' | 'streetAddress' | 'id' | 'isOffMarket' | 'isRental' | 'addressPathWithFallback'>;

export interface HomeAppraisalConnectionRequestProps {
  /** The object containing information about the property. */
  listing: ListingInfoType;

  /** Determines whether the component is being displayed in a modal. Default is *false* */
  isInModal?: boolean;

  /** Determines whether the component is being displayed in a popup. Default is *false* */
  isInPopup?: boolean;

  /** Determines whether the component is being displayed within a no estimate appraisal result. Default is *false* */
  noEstimate?: boolean;
}

export default function HomeAppraisalConnectionRequest({ listing, isInModal, isInPopup, noEstimate }: HomeAppraisalConnectionRequestProps) {
  const { t } = useTranslation(Namespaces.HOME_APPRAISAL);
  const { user, userLocation } = useUserContext();
  const { themeName } = useThemeContext();
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, reset, setValue } = useForm();
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const successfullySubmittedRef = useRef(false);

  const router = useRouter();
  const isWidget = router.pathname.startsWith('/widget/');
  const widgetClientId = (router.query.clientId as string) || '';
  const { streetAddress, id: listingId } = listing;

  // Pre-fill comment with the suggestion text as an actual value
  const defaultComment = listing?.streetAddress
    ? t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_COMMENTS_PLACEHOLDER_WITH_ADDRESS, {
      address: listing.streetAddress,
      defaultValue: `I'd like to get an accurate home estimate of my property, ${listing.streetAddress}.`,
    })
    : t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_COMMENTS_PLACEHOLDER, { defaultValue: 'I\'d like to get an accurate home estimate of my property.' });

  // Set default comment value when listing changes
  useEffect(() => {
    if (defaultComment) {
      setValue('sourceComments', defaultComment);
    }
  }, [defaultComment, setValue]);

  const sendRequest = async (values: Record<string, unknown>) => {
    setFormErrorMessage('');

    // Use form values if available, fall back to user context for pre-filled fields
    const firstName = (values.firstName as string || '').trim() || (user?.firstName || '');
    const email = (values.email as string || '').trim() || (user?.email || '');
    const phone = (values.phone as string || '').trim() || (user?.homePhone || '');

    if (!firstName || !email || !phone) {
      setFormErrorMessage('Please fill in all required fields.');
      return;
    }

    if (isInPopup) {
      trackEvent(noEstimate ? GTM_CLICK_APPRAISAL_NO_ESTIMATE_DETAIL_ESTIMATE_FOR_FREE_POPUP : GTM_CLICK_APPRAISAL_DETAIL_ESTIMATE_FOR_FREE_POPUP);
    }
    if (isWidget && widgetClientId) widgetEvents.trackAgentClick(widgetClientId);

    const sourceComments = (values.sourceComments as string || '').trim() || defaultComment;
    const properties = {
      ...values,
      firstName,
      lastName: user?.lastName || '',
      email,
      phone,
      listingId,
      streetAddress,
      sourceComments,
      userCountry: userLocation?.countryCode,
      theme: themeName,
      isHomeAppraisal: true,
      ...(isWidget && widgetClientId ? { widgetClientId } : {}),
    };

    createConnectionRequest(properties)
      .then(() => {
        setSuccessfullySubmitted(true);
        reset();
      })
      .catch(({ errors }: { errors: { attribute: string; message: string }[] }) => {
        errors.forEach(({ attribute, message }) => {
          if (attribute === 'homePhone') {
            setFormErrorMessage('Invalid phone number');
          } else if (attribute === 'email') {
            setFormErrorMessage('Invalid email');
          } else {
            setError(attribute, { message });
          }
        });
      });
  };

  // Used to access state during component unmounting
  useEffect(() => {
    successfullySubmittedRef.current = successfullySubmitted;
  }, [successfullySubmitted]);

  return (
    <>
      <form
        onSubmit={e => {
          clearErrors();
          setFormErrorMessage('');
          handleSubmit(sendRequest)(e);
        }}
        className={buildClassName(styles.component, isInModal && styles.modal)}
      >
        {!successfullySubmitted ?
          <>
            <h3>{t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_TITLE, { defaultValue: 'Get a more accurate, in-home estimate for free' })}</h3>
            <p className={styles.note}>{t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_DESCRIPTION, { defaultValue: 'Connect with our team to get a detailed home estimate for free!' })}</p>
            {formErrorMessage && <p className={styles['form-error']}>{formErrorMessage}</p>}
            <TextField
              placeholder={t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_FIRST_NAME_PLACEHOLDER, { defaultValue: 'First Name' })}
              {...register('firstName')}
              value={user?.firstName}
              errors={errors.firstName}
            />
            <TextField
              placeholder={t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_EMAIL_PLACEHOLDER, { defaultValue: 'Enter Your Email' })}
              {...register('email')}
              value={user?.email}
              errors={errors.email}
              onClick={() => trackEvent(GTM_CLICK_APPRAISAL_ENTER_EMAIL)}
            />
            <TextField
              placeholder={t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_PHONE_PLACEHOLDER, { defaultValue: 'Phone Number' })}
              {...register('phone')}
              value={user?.homePhone}
              errors={errors.phone}
            />
            <TextArea
              placeholder={defaultComment}
              {...register('sourceComments')}
              errors={errors.sourceComments}
            />
            <div className={styles['submit-button-container']}>
              <Button label={t(LOCALE_HOME_APPRAISAL_PATHS.CONNECTION_REQUEST_SUBMIT_BUTTON, { defaultValue: 'Get a Home Estimate for Free' })} type="submit" theme="primary" />
            </div>
          </>
          :
          <SuccessMessage />
        }
      </form>
      <Snackbar />
    </>
  );
}
